import type { ReactNode } from 'react';

import { Icon } from '@repo/ui';

export interface Tab {
  id: string;
  label: string;
  icon?: string;
  iconPosition?: 'left' | 'right';
  content?: ReactNode;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  navigationClassName?: string;
  contentClassName?: string;
  /**
   * If true, navigation bar will stretch full width of its container
   * and each tab button will take equal width. Default: true.
   */
  fullWidthNavigation?: boolean;
}

export function TabNavigation({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  navigationClassName = '',
  contentClassName = '',
  fullWidthNavigation = false,
}: TabNavigationProps) {
  // navigation items exclude content (for rendering buttons)
  const navigationItems = tabs.map(({ content, ...tab }) => tab);

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  // container width class depends on fullWidthNavigation
  const navContainerWidthClass = fullWidthNavigation ? 'w-full' : 'w-fit';

  return (
    <div className={className}>
      {/* Tab Navigation Buttons */}
      <div
        className={`flex space-x-1 bg-gray-100 dark:bg-xtnd-dark-800 border border-gray-200 dark:border-xtnd-dark-600 p-1 rounded-lg ${navContainerWidthClass} ${navigationClassName}`}
      >
        {navigationItems.map(tab => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            // make each button flex-1 when fullWidthNavigation so they share space equally
            className={`${
              fullWidthNavigation ? 'flex-1 flex justify-center items-center' : 'flex items-center'
            } px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-xtnd-blue text-white'
                : 'text-gray-600 hover:text-xtnd-dark dark:text-gray-300 dark:hover:text-xtnd-blue'
            }`}
            type="button"
          >
            {tab.icon && tab.iconPosition !== 'right' && (
              <Icon name={tab.icon as any} className="h-4 w-4 inline mr-2" />
            )}
            <span className="truncate">{tab.label}</span>
            {tab.icon && tab.iconPosition === 'right' && (
              <Icon name={tab.icon as any} className="h-4 w-4 inline ml-2" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content - Only render if content exists */}
      {activeTabData?.content !== undefined && (
        <div className={contentClassName}>{activeTabData.content}</div>
      )}
    </div>
  );
}
